import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAdminStore } from '../stores/adminStore';
import { Button } from '../components/common/Button';
import CreditTransactionFilterBar from '../components/admin/CreditTransactionFilterBar';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../components/table';

type CreditTransactionsLocationState = {
  userId?: string;
  userName?: string;
};

const AdminCreditTransactions: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);

  const location = useLocation();
  const navigate = useNavigate();
  const incomingState = (location.state || {}) as CreditTransactionsLocationState;

  const { creditTransactions, fetchCreditTransactions, fetchMoreCreditTransactions, hasMore } =
    useAdminStore();

  const [filters, setLocalFilters] = useState({
    search: '',
    actionType: '',
    startDate: '',
    endDate: '',
    userId: incomingState.userId ?? '',
  });
  const [userFilterLabel, setUserFilterLabel] = useState(incomingState.userName ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const [loading, setLoading] = useState(false);

  // Consume the navigation state once so a refresh / back navigation does not
  // re-apply a stale user filter.
  useEffect(() => {
    if (incomingState.userId) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => window.clearTimeout(timeout);
  }, [filters.search]);

  useEffect(() => {
    setLoading(true);
    fetchCreditTransactions({
      filters: { ...filters, search: debouncedSearch },
      offsetCount: 0,
      isLoadMore: false,
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters.actionType, filters.startDate, filters.endDate, filters.userId]);

  const handleFilterChange = (name: keyof typeof filters, value: string) => {
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearUserFilter = () => {
    setUserFilterLabel('');
    setLocalFilters(prev => ({ ...prev, userId: '' }));
  };

  const handleLoadMore = async () => {
    setLoading(true);
    await fetchMoreCreditTransactions();
    setLoading(false);
  };

  return (
    <>
      <div className="w-full pt-4 pb-5 flex flex-col md:w-2/3 md:px-1 md:pr-8 md:pb-8">
        <h1 className="text-5xl font-bold hyphens-auto">
          {t('creditTransactions.title', { ns: 'admin' })}
        </h1>
      </div>

      <div className="overflow-hidden rounded-2xl bg-white border border-gray-300 shadow-sm dark:border-white/[0.08] dark:bg-white/[0.03] transition-all">
        <CreditTransactionFilterBar filters={filters} onFilterChange={handleFilterChange} />

        {filters.userId && (
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-600">
              {t('creditTransactions.filteredByUser', { ns: 'admin' })}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              {userFilterLabel || t('creditTransactions.filteredByUserFallback', { ns: 'admin' })}
              <button
                type="button"
                onClick={clearUserFilter}
                className="text-primary/70 hover:text-primary transition-colors"
                aria-label={t('creditTransactions.clearUserFilter', { ns: 'admin' })}
              >
                <X size={14} />
              </button>
            </span>
          </div>
        )}

        <div className="max-w-full overflow-x-auto">
          <Table className="w-full">
            <TableHeader className="bg-gray-50 dark:bg-white/[0.05]">
              <TableRow>
                {['date', 'user', 'reason', 'type', 'amount', 'balance'].map(field => (
                  <TableCell
                    key={field}
                    isHeader
                    className="text-start px-6 py-4 font-semibold uppercase text-gray-600 text-[0.75rem] tracking-wide dark:text-gray-400"
                  >
                    {t(`creditTransactions.columns.${field}`, { ns: 'admin' })}
                  </TableCell>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
              {creditTransactions.map(transaction => (
                <TableRow key={transaction.id}>
                  <TableCell className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                    {new Date(transaction.created_at).toLocaleString()}
                  </TableCell>

                  <TableCell className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {transaction.first_name} {transaction.last_name}
                    </div>
                    {transaction.email && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {transaction.email}
                      </div>
                    )}
                  </TableCell>

                  <TableCell className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                    {transaction.reason ||
                      t(`creditTransactions.actionTypes.${transaction.action_type}`, {
                        ns: 'admin',
                      })}
                  </TableCell>

                  <TableCell className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                    <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-white/[0.08] dark:text-gray-300">
                      {t(`creditTransactions.actionTypes.${transaction.action_type}`, {
                        ns: 'admin',
                      })}
                    </span>
                  </TableCell>

                  <TableCell className="px-6 py-4 text-sm font-medium">
                    <span className={transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}>
                      {transaction.amount > 0 ? '+' : ''}
                      {transaction.amount}
                    </span>
                  </TableCell>

                  <TableCell className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                    {transaction.balance_after}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {hasMore?.creditTransactions && (
          <div className="py-4 flex justify-center">
            <Button className="px-8" onClick={handleLoadMore} disabled={loading}>
              {loading
                ? t('loading', { ns: 'admin', defaultValue: 'Chargement...' })
                : t('loadMore', { ns: 'admin', defaultValue: 'Charger plus' })}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminCreditTransactions;
